import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { Circle, Crosshair, Expand, Globe, MapPinOff, X } from 'lucide-react';
import {
    entfernungText,
    hatStandort,
    ortText,
    rolleFarbe,
    rolleLabel,
    umkreisText,
    type Punkt,
    type TalentEintrag,
} from '../../services/talentpool/talentpoolLogik';

/**
 * Karte des Talentpools: eine Nadel je Bewerber am Standort, farbig nach
 * Rolle. Der gewählte zeigt seinen Umkreis als Ring (`umkreis_km`), ein
 * Schalter blendet die Ringe aller ein; „bundesweit“ hat keinen Ring,
 * sondern ein Abzeichen an der Nadel. Ein Klick auf eine freie Stelle setzt
 * den Suchpunkt (die Liste rechnet daraus Entfernung und „erreicht“), ein
 * Klick auf den Suchpunkt nimmt ihn wieder weg.
 *
 * Aufbau wie die CRM-Karte (CrmMapView): die Karte wird EINMAL erzeugt und
 * nie wegen eines Filters neu gebaut; Ringe, Nadeln und Suchpunkt liegen in
 * je einer LayerGroup, die gezielt geleert wird. Kacheln: derselbe
 * OSM-Weg, Namensnennung in der Legende. Alle Ableitungen kommen fertig aus
 * `talentpoolLogik` herein.
 */

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Deutschland-Ausschnitt beim Öffnen (wie CrmMapView). */
const START_MITTE: [number, number] = [51.1657, 10.4515];
const START_ZOOM = 6;

/** HTML-Text sicher einbetten. DivIcons und Tooltips sind rohes HTML — Namen kommen von der Website. */
const esc = (s: unknown): string =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );

const GLOBUS_SVG = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4338ca" stroke-width="2.4" aria-hidden="true">
  <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20"/></svg>`;

/** Nadel (Tropfen mit weißem Kern), Spitze am Standort. Gewählt: größer und dunkel umrandet. */
const nadelIcon = (farbe: string, gewaehlt: boolean, bundesweit: boolean): L.DivIcon => {
    const b = gewaehlt ? 32 : 24;
    const h = Math.round(b * 4 / 3);
    const rand = gewaehlt ? '#0f172a' : '#ffffff';
    const abzeichen = bundesweit ? `<span class="tp-karte-bw" title="bundesweit">${GLOBUS_SVG}</span>` : '';
    return L.divIcon({
        className: 'tp-karte-icon',
        html: `<span class="tp-karte-wrap">
            <svg width="${b}" height="${h}" viewBox="0 0 24 32" aria-hidden="true">
              <path d="M12 1.2C6.1 1.2 1.6 5.6 1.6 11.3c0 7.4 8.9 17.9 9.8 19a.8.8 0 0 0 1.2 0c.9-1.1 9.8-11.6 9.8-19C22.4 5.6 17.9 1.2 12 1.2z"
                fill="${farbe}" stroke="${rand}" stroke-width="${gewaehlt ? 2.2 : 1.8}"/>
              <circle cx="12" cy="11.2" r="4" fill="#ffffff"/>
            </svg>${abzeichen}</span>`,
        iconSize: [b, h],
        iconAnchor: [b / 2, h - 1],
        tooltipAnchor: [0, -h + 4],
    });
};

/** Suchpunkt: Fadenkreuz wie die Ausschreibungen der CRM-Karte, in Schiefer. */
const suchIcon = L.divIcon({
    className: 'tp-karte-icon',
    html: `<span class="tp-karte-wrap"><svg width="30" height="30" viewBox="0 0 26 26" aria-hidden="true">
        <circle cx="13" cy="13" r="9" fill="rgba(255,255,255,.75)" stroke="#0f172a" stroke-width="2"/>
        <circle cx="13" cy="13" r="2.5" fill="#e11d48"/>
        <path d="M13 1v5M13 20v5M1 13h5M20 13h5" stroke="#0f172a" stroke-width="2" stroke-linecap="round"/>
      </svg></span>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});

const tooltipHtml = (e: TalentEintrag, mitSuchpunkt: boolean): string => {
    const z = e.zeile;
    const zeilen = [`<strong>${esc(z.name)}</strong>`, esc(rolleLabel(z.rolle))];
    const ort = ortText(z);
    if (ort) zeilen.push(esc(ort));
    zeilen.push(esc(umkreisText(z)));
    if (mitSuchpunkt && e.km !== null) {
        const urteil = e.erreicht === true ? 'erreicht den Suchpunkt' : e.erreicht === false ? 'zu weit' : 'Umkreis unklar';
        zeilen.push(`${esc(entfernungText(e.km))} · ${urteil}`);
    }
    return `<div class="text-[11px] leading-snug">${zeilen.join('<br/>')}</div>`;
};

interface Props {
    /** Gefilterte Bewerbungen in Listenreihenfolge; ohne Standort werden sie hier übersprungen. */
    eintraege: TalentEintrag[];
    gewaehltId: string | null;
    /** Wechselt `nr`, schwenkt die Karte auf diese Bewerbung (Auswahl aus der Liste). */
    fokus: { id: string; nr: number } | null;
    suchpunkt: Punkt | null;
    alleRinge: boolean;
    /** Wie viele davon den Suchpunkt erreichen. */
    anzahlErreicht: number;
    legende: { label: string; farbe: string }[];
    onWaehle: (id: string) => void;
    onSuchpunkt: (punkt: Punkt | null) => void;
    onAlleRinge: (an: boolean) => void;
}

export const TalentpoolKarte: React.FC<Props> = ({
    eintraege, gewaehltId, fokus, suchpunkt, alleRinge, anzahlErreicht, legende, onWaehle, onSuchpunkt, onAlleRinge,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const ringeRef = useRef<L.LayerGroup | null>(null);
    const nadelnRef = useRef<L.LayerGroup | null>(null);
    const suchRef = useRef<L.LayerGroup | null>(null);

    // Rückrufe über Refs: die Leaflet-Ereignisse werden einmal gebunden und
    // sollen trotzdem immer den aktuellen Rückruf treffen.
    const onWaehleRef = useRef(onWaehle);
    const onSuchpunktRef = useRef(onSuchpunkt);
    useEffect(() => { onWaehleRef.current = onWaehle; }, [onWaehle]);
    useEffect(() => { onSuchpunktRef.current = onSuchpunkt; }, [onSuchpunkt]);

    // ── Karte erzeugen (genau einmal) ───────────────────────────────────────
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        const map = L.map(containerRef.current, { zoomControl: false, attributionControl: false })
            .setView(START_MITTE, START_ZOOM);
        L.control.zoom({ position: 'bottomright' }).addTo(map);
        L.tileLayer(TILE_URL, { maxZoom: 19 }).addTo(map);
        ringeRef.current = L.layerGroup().addTo(map);
        nadelnRef.current = L.layerGroup().addTo(map);
        suchRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        // Klick auf eine freie Stelle: Suchpunkt setzen bzw. versetzen. Nadeln
        // fangen ihren Klick selbst ab (Marker blubbern nicht), Ringe sind
        // nicht interaktiv — der Klick in einen Ring setzt also den Punkt.
        map.on('click', (e: L.LeafletMouseEvent) => {
            const p = e.latlng.wrap();
            onSuchpunktRef.current({ lat: p.lat, lng: p.lng });
        });

        // Leaflet misst beim Erzeugen die Containergröße; steht die Ansicht
        // noch im Aufbau, ist sie 0 und die Karte bleibt grau. Danach folgt
        // sie jeder Größenänderung der Spalte.
        const t = window.setTimeout(() => map.invalidateSize(), 120);
        const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.invalidateSize());
        beobachter?.observe(containerRef.current);

        return () => {
            window.clearTimeout(t);
            beobachter?.disconnect();
            map.off();
            map.remove();
            mapRef.current = null;
            ringeRef.current = null;
            nadelnRef.current = null;
            suchRef.current = null;
        };
    }, []);

    // ── Ringe und Nadeln ────────────────────────────────────────────────────
    const mitSuchpunkt = suchpunkt !== null;
    useEffect(() => {
        const ringe = ringeRef.current;
        const nadeln = nadelnRef.current;
        if (!ringe || !nadeln) return;
        ringe.clearLayers();
        nadeln.clearLayers();
        for (const e of eintraege) {
            const z = e.zeile;
            if (!hatStandort(z)) continue;
            const ort: [number, number] = [z.lat as number, z.lng as number];
            const farbe = rolleFarbe(z.rolle);
            const gewaehlt = z.id === gewaehltId;
            const km = z.umkreis_km;
            if ((gewaehlt || alleRinge) && !z.bundesweit && typeof km === 'number' && km > 0) {
                ringe.addLayer(L.circle(ort, {
                    radius: km * 1000,
                    color: farbe,
                    weight: gewaehlt ? 2 : 1,
                    opacity: gewaehlt ? 0.9 : 0.5,
                    fillColor: farbe,
                    fillOpacity: gewaehlt ? 0.1 : 0.04,
                    // Information, kein Bedienelement: ein Klick in den Ring
                    // setzt den Suchpunkt wie überall sonst auf der Karte.
                    interactive: false,
                }));
            }
            const nadel = L.marker(ort, {
                icon: nadelIcon(farbe, gewaehlt, z.bundesweit),
                zIndexOffset: gewaehlt ? 1000 : 0,
                alt: z.name,
            });
            nadel.bindTooltip(tooltipHtml(e, mitSuchpunkt), { direction: 'top' });
            nadel.on('click', () => onWaehleRef.current(z.id));
            nadeln.addLayer(nadel);
        }
    }, [eintraege, gewaehltId, alleRinge, mitSuchpunkt]);

    // ── Suchpunkt ───────────────────────────────────────────────────────────
    useEffect(() => {
        const gruppe = suchRef.current;
        if (!gruppe) return;
        gruppe.clearLayers();
        if (!suchpunkt) return;
        const marker = L.marker([suchpunkt.lat, suchpunkt.lng], { icon: suchIcon, zIndexOffset: 2000, alt: 'Suchpunkt' });
        marker.bindTooltip('Suchpunkt — klicken zum Entfernen', { direction: 'top', offset: [0, -14] });
        marker.on('click', () => onSuchpunktRef.current(null));
        gruppe.addLayer(marker);
    }, [suchpunkt]);

    // ── Auswahl aus der Liste: hinschwenken ─────────────────────────────────
    // Nur wenn `fokus.nr` wechselt — ein Klick auf die Nadel selbst soll die
    // Karte nicht verschieben. Mit Ring: der ganze Umkreis ins Bild.
    const eintraegeRef = useRef(eintraege);
    useEffect(() => { eintraegeRef.current = eintraege; }, [eintraege]);
    const fokusNr = fokus?.nr ?? 0;
    const fokusId = fokus?.id ?? null;
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !fokusId) return;
        const z = eintraegeRef.current.find(e => e.zeile.id === fokusId)?.zeile;
        if (!z || !hatStandort(z)) return;
        const ort = L.latLng(z.lat as number, z.lng as number);
        if (!z.bundesweit && typeof z.umkreis_km === 'number' && z.umkreis_km > 0) {
            map.fitBounds(ort.toBounds(z.umkreis_km * 2000), { padding: [30, 30], maxZoom: 11 });
        } else {
            map.setView(ort, Math.max(map.getZoom(), 9));
        }
    }, [fokusNr, fokusId]);

    /** Alle sichtbaren Nadeln (und den Suchpunkt) ins Bild. */
    const alleZeigen = () => {
        const map = mapRef.current;
        if (!map) return;
        const punkte: L.LatLngExpression[] = eintraege
            .filter(e => hatStandort(e.zeile))
            .map(e => [e.zeile.lat as number, e.zeile.lng as number]);
        if (suchpunkt) punkte.push([suchpunkt.lat, suchpunkt.lng]);
        if (punkte.length === 0) {
            map.setView(START_MITTE, START_ZOOM);
            return;
        }
        map.fitBounds(L.latLngBounds(punkte), { padding: [40, 40], maxZoom: 10 });
    };

    const mitNadel = eintraege.filter(e => hatStandort(e.zeile)).length;
    const ohneNadel = eintraege.length - mitNadel;

    return (
        <div className="relative isolate w-full h-full min-h-[360px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <style>{KARTE_CSS}</style>

            <div ref={containerRef} className="absolute inset-0 z-0" aria-label="Karte der Bewerber" role="application" />

            {/* ── Bedienung oben ── */}
            <div className="absolute top-3 left-3 right-3 z-[400] pointer-events-none flex flex-wrap items-start gap-2">
                <div className="glass rounded-2xl px-3 py-2 flex flex-wrap items-center gap-2 pointer-events-auto max-w-full">
                    <button
                        type="button"
                        onClick={() => onAlleRinge(!alleRinge)}
                        aria-pressed={alleRinge}
                        className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-colors flex items-center gap-1 ${
                            alleRinge ? 'bg-indigo-500/10 border-indigo-200 text-indigo-700' : 'border-transparent text-slate-600 hover:bg-white/70'
                        }`}
                    >
                        <Circle className="w-3.5 h-3.5" aria-hidden /> Umkreis aller
                    </button>
                    <button
                        type="button"
                        onClick={alleZeigen}
                        title="Alle Nadeln ins Bild"
                        className="glass-button rounded-lg px-2 py-1 text-[11px] font-bold text-slate-600 flex items-center gap-1"
                    >
                        <Expand className="w-3.5 h-3.5" aria-hidden /> Alle zeigen
                    </button>
                    {suchpunkt ? (
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-700">
                            <Crosshair className="w-3.5 h-3.5 text-rose-600" aria-hidden />
                            <span aria-live="polite">
                                <strong className="tabular-nums">{anzahlErreicht}</strong> von {eintraege.length} erreichen den Suchpunkt
                            </span>
                            <button
                                type="button"
                                onClick={() => onSuchpunkt(null)}
                                className="glass-button rounded-lg px-2 py-1 font-bold text-slate-600 flex items-center gap-1"
                            >
                                <X className="w-3 h-3" aria-hidden /> Suchpunkt entfernen
                            </button>
                        </span>
                    ) : (
                        <span className="text-[11px] text-slate-500">Klick in die Karte setzt einen Suchpunkt — wer erreicht ihn?</span>
                    )}
                </div>
            </div>

            {/* ── Legende + Namensnennung unten links ── */}
            <div className="absolute bottom-3 left-3 z-[400] pointer-events-none">
                <div className="glass rounded-2xl px-3 py-2 max-w-[260px] pointer-events-auto">
                    <ul className="space-y-1">
                        {legende.map(l => (
                            <li key={l.label} className="flex items-center gap-2 text-[11px] text-slate-600">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-white shadow-sm" style={{ backgroundColor: l.farbe }} aria-hidden />
                                {l.label}
                            </li>
                        ))}
                        <li className="flex items-center gap-2 text-[11px] text-slate-600">
                            <span className="w-2.5 h-2.5 flex items-center justify-center shrink-0" aria-hidden><Globe className="w-2.5 h-2.5 text-indigo-700" /></span>
                            bundesweit (ohne Ring)
                        </li>
                    </ul>
                    {ohneNadel > 0 && (
                        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-700">
                            <MapPinOff className="w-3 h-3 shrink-0" aria-hidden />
                            {ohneNadel === 1 ? '1 Bewerbung ohne Standort' : `${ohneNadel} Bewerbungen ohne Standort`} — nur in der Liste
                        </p>
                    )}
                    <p className="mt-1.5 pt-1.5 border-t border-slate-200/70 text-[10px] text-slate-400">© OpenStreetMap contributors</p>
                </div>
            </div>
        </div>
    );
};

/**
 * Nadel-CSS. Bewusst hier und nicht in index.css (wie MARKER_CSS der
 * CRM-Karte): die Regeln gehören ausschließlich zu diesen DivIcons.
 */
const KARTE_CSS = `
.tp-karte-icon { background: none; border: none; }
.tp-karte-wrap { position: relative; display: block; width: 100%; height: 100%; }
.tp-karte-wrap svg { display: block; filter: drop-shadow(0 1px 1.5px rgba(15,23,42,.35)); }
.tp-karte-bw {
  position: absolute; top: -5px; right: -7px;
  width: 14px; height: 14px; border-radius: 9999px;
  display: flex; align-items: center; justify-content: center;
  background: #eef2ff; border: 1.5px solid #4338ca;
  box-shadow: 0 1px 2px rgba(0,0,0,.25);
}
.tp-karte-bw svg { filter: none; }
`;
